import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../../../context/authcontext";
import { API_BASE_URL } from "../../../environment";
import { getAuthHeaders } from "../../../utils/authUtils";
import { cachedRequest } from "../../../utils/requestCache";
import { useFilterOptions, UseFilterOptionsFlags } from "../../../hooks/useFilterOptions";
import { languageOptions } from "../../../components/language";
import { currencyOptions } from "../../../components/currency";
import { timezoneOptions } from "../../../components/timezone";
import { LifecycleService } from "../leads/lifecycle.service";
import { DealService } from "../deals/deal.service";
import { getLifecycleTypeName } from "../../../utils/lifecycleUtils";

export type LifecycleEntityType = "lead" | "pipeline" | "deal" | "proposal" | "contract";

interface LifecycleFilterDropdownProps {
  entityType: LifecycleEntityType;
  onFilterChange: (filters: Record<string, any>) => void;
  currentFilters: Record<string, any>;
  loading?: boolean;
}

interface StatusOption {
  id: number;
  label: string;
}

const EXCLUDED_KEYS = [
  "searchTerm",
  "dateRange",
  "statusFilter",
  "archivedFilter",
  "sortBy",
  "sortOrder",
  "industryFilter",
  "countryFilter",
  "sort_by",
];

const FIELD_KEY_MAP: Record<string, string> = {
  Priority: "priority",
  Phone: "phone",
  Email: "email",
  Currency: "currency_code",
  Timezone: "time_zone",
  Language: "preferred_language",
  Company: "company_id",
  "Assigned User": "assigned_user_id",
  "Assigned Team": "assigned_team_id",
  Tags: "tags",
  Source: "lifecycle_source_id",
  "Related Leads": "related_lead_id",
  "Related Pipelines": "related_pipeline_id",
  "Related Deals": "related_deal_id",
  "Related Proposal": "related_proposal_id",
  "Start Date & Time": "start_datetime",
  "Expiration Date & Time": "expiration_datetime",
  "Due Date & Time": "due_datetime",
};

const getFieldKey = (field: string) =>
  FIELD_KEY_MAP[field] || field.toLowerCase().replace(/ /g, "_");

const getFieldDisplayLabel = (field: string) =>
  field === "Related Proposal"
    ? `Related ${getLifecycleTypeName("PROPOSAL")}`
    : field;

const ENTITY_FIELDS: Record<LifecycleEntityType, string[]> = {
  lead: ["Priority", "Phone", "Email", "Currency", "Timezone", "Language", "Company", "Assigned User", "Assigned Team", "Tags", "Source"],
  pipeline: ["Priority", "Phone", "Email", "Currency", "Company", "Assigned User", "Assigned Team", "Tags", "Source", "Related Leads"],
  deal: ["Priority", "Phone", "Email", "Currency", "Company", "Assigned User", "Assigned Team", "Tags", "Source", "Related Leads", "Related Pipelines"],
  proposal: ["Phone", "Email", "Currency", "Company", "Assigned User", "Assigned Team", "Tags", "Related Deals", "Start Date & Time", "Expiration Date & Time"],
  contract: ["Phone", "Email", "Currency", "Company", "Assigned User", "Assigned Team", "Tags", "Related Proposal", "Due Date & Time", "Expiration Date & Time"],
};

const ENTITY_CLEARED_FIELDS: Record<LifecycleEntityType, string[]> = {
  lead: ["priority", "phone", "email", "currency_code", "time_zone", "preferred_language", "company_id", "assigned_user_id", "assigned_team_id", "tags", "lifecycle_source_id"],
  pipeline: ["priority", "phone", "email", "currency_code", "company_id", "assigned_user_id", "assigned_team_id", "tags", "lifecycle_source_id", "related_lead_id"],
  deal: ["priority", "phone", "email", "currency_code", "company_id", "assigned_user_id", "assigned_team_id", "tags", "lifecycle_source_id", "related_lead_id", "related_pipeline_id"],
  proposal: ["phone", "email", "currency_code", "company_id", "assigned_user_id", "assigned_team_id", "tags", "lifecycle_source_id", "related_deal_id", "start_datetime", "expiration_datetime"],
  contract: ["phone", "email", "currency_code", "company_id", "assigned_user_id", "assigned_team_id", "tags", "related_proposal_id", "due_datetime", "expiration_datetime"],
};

const HAS_ARCHIVED: Record<LifecycleEntityType, boolean> = { lead: true, pipeline: true, deal: true, proposal: false, contract: false };
const HAS_IS_ACTIVE: Record<LifecycleEntityType, boolean> = { lead: true, pipeline: false, deal: false, proposal: false, contract: false };
const DOT_BADGE: Record<LifecycleEntityType, boolean> = { lead: true, pipeline: true, deal: false, proposal: false, contract: false };
const SHOW_FIELD_PREVIEW: Record<LifecycleEntityType, boolean> = { lead: true, pipeline: true, deal: false, proposal: false, contract: false };
const CONDITIONAL_FOOTER: Record<LifecycleEntityType, boolean> = { lead: true, pipeline: true, deal: false, proposal: false, contract: false };
export const STATUS_FIELD_LABEL: Record<LifecycleEntityType, string> = { lead: "Stage", pipeline: "Stage", deal: "Stage", proposal: "Status", contract: "Type" };
const STATUS_ALL_LABEL: Record<LifecycleEntityType, string> = { lead: "All Stages", pipeline: "All Stages", deal: "All Stages", proposal: "All Statuses", contract: "All Types" };

const FILTER_OPTION_FLAGS: Record<LifecycleEntityType, UseFilterOptionsFlags> = {
  lead: { teams: true, users: true, companies: true, lifecycleSources: true },
  pipeline: { teams: true, users: true, companies: true, lifecycleSources: true, leads: true },
  deal: { teams: true, users: true, companies: true, lifecycleSources: true, leads: true, pipelines: true },
  proposal: { teams: true, users: true, companies: true, deals: true, lifecycleSources: true },
  contract: { teams: true, users: true, companies: true, proposals: true, lifecycleSources: true },
};

export const fetchStatusOptions = async (
  entityType: LifecycleEntityType,
  tenantId: number
): Promise<StatusOption[]> => {
  switch (entityType) {
    case "lead": {
      const stages = await LifecycleService.getLeadStages(tenantId);
      return stages.map((s) => ({ id: s.lifecycle_type_stage_id, label: s.stage_name }));
    }
    case "pipeline": {
      const lifecycleTypes = await LifecycleService.getLifecycleTypes(tenantId);
      const pipelineType = lifecycleTypes.find((t) => t.lifecycle_type_key === "PIPELINE");
      if (!pipelineType) {
        console.warn("No lifecycle type found for PIPELINE");
        return [];
      }
      const stages = await LifecycleService.getLifecycleStages(pipelineType.lifecycle_type_id, tenantId);
      return stages.map((s) => ({ id: s.lifecycle_type_stage_id, label: s.stage_name }));
    }
    case "deal": {
      const stages = await DealService.getDealStages(tenantId);
      return stages.map((s: any) => ({ id: s.lifecycle_type_stage_id, label: s.stage_name }));
    }
    case "proposal": {
      const results = await cachedRequest(`proposal-statuses:${tenantId}`, async () => {
        const authHeader = localStorage.getItem("authCred");
        if (!authHeader) return [];

        const response = await fetch(
          `${API_BASE_URL}/proposal-status/?tenant_id=${tenantId}&response_mode=light`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch proposal statuses");
        }

        const data = await response.json();
        const fetched = Array.isArray(data.results) ? data.results : [];
        return fetched.sort((a: any, b: any) => a.proposal_status_order - b.proposal_status_order);
      });
      return results.map((s: any) => ({ id: s.proposal_status_id, label: s.proposal_status_display_name }));
    }
    case "contract": {
      const results = await cachedRequest(`contract-types:${tenantId}`, async () => {
        const authHeader = localStorage.getItem("authCred");
        if (!authHeader) return [];

        const response = await fetch(
          `${API_BASE_URL}/contract-type/?tenant_id=${tenantId}&response_mode=light`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch contract types");
        }

        const data = await response.json();
        const fetched = Array.isArray(data.results) ? data.results : [];
        return fetched.sort((a: any, b: any) => a.contract_type_order - b.contract_type_order);
      });
      return results.map((s: any) => ({ id: s.contract_type_id, label: s.contract_type_display_name }));
    }
    default:
      return [];
  }
};

const LifecycleFilterDropdown: React.FC<LifecycleFilterDropdownProps> = ({
  entityType,
  onFilterChange,
  currentFilters,
  loading = false,
}) => {
  const { selectedTenantId } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const { teams, users, companies, lifecycleSources, leads, pipelines, deals, proposals } = useFilterOptions(
    selectedTenantId,
    FILTER_OPTION_FLAGS[entityType],
    isOpen
  );
  const [statuses, setStatuses] = useState<StatusOption[]>([]);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hasArchived = HAS_ARCHIVED[entityType];
  const hasIsActive = HAS_IS_ACTIVE[entityType];
  const useDotBadge = DOT_BADGE[entityType];
  const showFieldPreview = SHOW_FIELD_PREVIEW[entityType];
  const conditionalFooter = CONDITIONAL_FOOTER[entityType];
  const fields = ENTITY_FIELDS[entityType];

  const [statusFilter, setStatusFilter] = useState<number | null>(currentFilters.statusFilter ?? null);
  const [archivedFilter, setArchivedFilter] = useState<string>(currentFilters.archivedFilter || "no");
  const [isActiveFilter, setIsActiveFilter] = useState<string>(
    currentFilters.is_active === true ? "true" : currentFilters.is_active === false ? "false" : "both"
  );
  const [dynamicFilters, setDynamicFilters] = useState<Record<string, any>>({});
  const [textFilters, setTextFilters] = useState<Record<string, string>>({});
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});

  useEffect(() => {
    setStatusFilter(currentFilters.statusFilter ?? null);
    setArchivedFilter(currentFilters.archivedFilter || "no");
    setIsActiveFilter(
      currentFilters.is_active === true ? "true" : currentFilters.is_active === false ? "false" : "both"
    );

    const dyn: Record<string, any> = {};
    Object.keys(currentFilters).forEach((key) => {
      if (!EXCLUDED_KEYS.includes(key)) {
        dyn[key] = currentFilters[key];
      }
    });
    setDynamicFilters(dyn);
    setTextFilters((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(
          ([key]) => dyn[key] !== undefined && dyn[key] !== null && dyn[key] !== ""
        )
      );
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [currentFilters]);

  const fetchStatuses = async () => {
    if (!selectedTenantId) return;

    setIsLoadingStatuses(true);
    try {
      const options = await fetchStatusOptions(entityType, parseInt(selectedTenantId as string));
      setStatuses(options);
    } catch (error) {
      console.error("Error fetching status options:", error);
    } finally {
      setIsLoadingStatuses(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchStatuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantId, isOpen, entityType]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isOpen) e.currentTarget.blur();
    setIsOpen(!isOpen);
  };

  const toggleFilterExpansion = (filterKey: string) => {
    const newExpandedFilters = new Set(expandedFilters);
    if (newExpandedFilters.has(filterKey)) {
      newExpandedFilters.delete(filterKey);
    } else {
      newExpandedFilters.add(filterKey);
    }
    setExpandedFilters(newExpandedFilters);
  };

  const handleStatusChange = (stageId: number | null) => {
    setStatusFilter(stageId);
    onFilterChange({
      ...dynamicFilters,
      statusFilter: stageId,
      ...(hasArchived ? { archivedFilter } : {}),
    });
  };

  const handleArchivedChange = (archived: string) => {
    setArchivedFilter(archived);
    onFilterChange({
      ...dynamicFilters,
      statusFilter,
      archivedFilter: archived,
    });
  };

  const handleIsActiveChange = (active: string) => {
    setIsActiveFilter(active);
    let is_active_val: boolean | undefined = undefined;
    if (active === "true") is_active_val = true;
    else if (active === "false") is_active_val = false;

    const updatedFilters = { ...dynamicFilters, is_active: is_active_val };
    setDynamicFilters(updatedFilters);
    onFilterChange({
      ...updatedFilters,
      statusFilter,
      ...(hasArchived ? { archivedFilter } : {}),
    });
  };

  const handleDynamicFilterChange = (key: string, value: any) => {
    const updatedFilters = { ...dynamicFilters, [key]: value };
    setDynamicFilters(updatedFilters);
    onFilterChange({
      ...updatedFilters,
      statusFilter,
      ...(hasArchived ? { archivedFilter } : {}),
    });
  };

  const handleTextChange = (key: string, value: string) => {
    setTextFilters((prev) => ({ ...prev, [key]: value }));

    if (debounceTimers.current[key]) {
      clearTimeout(debounceTimers.current[key]);
    }

    debounceTimers.current[key] = setTimeout(() => {
      handleDynamicFilterChange(key, value);
    }, 2000);
  };

  const resetFilters = () => {
    setStatusFilter(null);
    setArchivedFilter("no");
    setIsActiveFilter("both");
    setDynamicFilters({});
    setTextFilters({});

    const clearedFilters: Record<string, any> = {};
    ENTITY_CLEARED_FIELDS[entityType].forEach((f) => {
      clearedFilters[f] = undefined;
    });

    onFilterChange({
      ...clearedFilters,
      statusFilter: null,
      ...(hasArchived ? { archivedFilter: "no" } : {}),
      ...(hasIsActive ? { is_active: undefined } : {}),
    });
    setIsOpen(false);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (currentFilters.statusFilter !== null && currentFilters.statusFilter !== undefined) count++;
    if (hasArchived && currentFilters.archivedFilter && currentFilters.archivedFilter !== "no") count++;

    Object.keys(currentFilters).forEach((key) => {
      if (!EXCLUDED_KEYS.includes(key)) {
        const val = currentFilters[key];
        if (Array.isArray(val) && val.length === 0) return;
        if (val !== null && val !== undefined && val !== "") count++;
      }
    });

    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  const truncateText = (text: string, maxLength: number = 15) => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  const getCurrentStatusName = () => {
    if (statusFilter === null) return STATUS_ALL_LABEL[entityType];
    const status = statuses.find((s) => s.id === statusFilter);
    return status?.label || STATUS_ALL_LABEL[entityType];
  };

  return (
    <div className="position-relative" ref={dropdownRef}>
      <button
        type="button"
        className={`btn ${isOpen ? "btn-primary" : "btn-outline-light"} shadow dropdown-toggle${useDotBadge ? " position-relative" : ""}`}
        onClick={toggleDropdown}
      >
        {!useDotBadge && activeFilterCount > 0 && (
          <span
            className="position-absolute"
            style={{
              top: "-4px",
              right: "-4px",
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: "red",
            }}
          ></span>
        )}
        <img
          src="/assets/img/icons/filter.svg"
          alt="Filter"
          className="me-1"
          style={{
            width: "16px",
            height: "16px",
            filter: isOpen ? "brightness(0) invert(1)" : undefined,
          }}
        />
        <span className="me-2">Filter</span>
        {useDotBadge && activeFilterCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle">
            <span className="visually-hidden">New alerts</span>
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="dropdown-menu show"
          style={{
            width: "280px",
            maxHeight: "400px",
            zIndex: 9999,
            position: "absolute",
            top: "100%",
            right: "0",
            left: "auto",
            marginTop: "0.125rem",
            backgroundColor: "white",
            border: "1px solid rgba(0,0,0,.15)",
            borderRadius: "0.375rem",
            boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div className="p-3 pb-0">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <img
                  src="/assets/img/icons/filter.svg"
                  alt="Filter"
                  className="me-1"
                  style={{ width: "16px", height: "16px" }}
                />
                {STATUS_FIELD_LABEL[entityType]} Filter
              </h6>
            </div>
          </div>

          {/* Scrollable Filter Content */}
          <div className="px-3" style={{ maxHeight: "300px", overflowY: "auto", flex: 1 }}>
            <div className="space-y-2">
              {/* Status/Type Filter */}
              <div className="border rounded">
                <button
                  type="button"
                  className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
                  onClick={() => toggleFilterExpansion("status")}
                  disabled={loading || isLoadingStatuses}
                >
                  <span className="small text-dark">
                    <strong>{STATUS_FIELD_LABEL[entityType]}</strong>
                  </span>
                  <div className="d-flex align-items-center">
                    {getCurrentStatusName() !== STATUS_ALL_LABEL[entityType] && (
                      <span className="small text-muted me-2">{getCurrentStatusName()}</span>
                    )}
                    <i className={`ti ti-chevron-${expandedFilters.has("status") ? "up" : "down"} small text-dark`}></i>
                  </div>
                </button>
                {expandedFilters.has("status") && (
                  <div className="p-2 border-top">
                    <select
                      className="form-select form-select-sm"
                      value={statusFilter ?? ""}
                      onChange={(e) => handleStatusChange(e.target.value ? Number(e.target.value) : null)}
                      disabled={loading || isLoadingStatuses}
                    >
                      <option value="">{STATUS_ALL_LABEL[entityType]}</option>
                      {statuses.map((status) => (
                        <option key={status.id} value={status.id}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                    {isLoadingStatuses && (
                      <div className="text-center mt-2">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Archived Filter */}
              {hasArchived && (
                <div className="border rounded mt-2">
                  <button
                    type="button"
                    className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
                    onClick={() => toggleFilterExpansion("archived")}
                    disabled={loading}
                  >
                    <span className="small text-dark">
                      <strong>Archived</strong>
                    </span>
                    <div className="d-flex align-items-center">
                      {archivedFilter !== "no" && (
                        <span className="small text-muted me-2">
                          {archivedFilter === "yes" ? "Yes" : archivedFilter === "both" ? "Both" : "No"}
                        </span>
                      )}
                      <i className={`ti ti-chevron-${expandedFilters.has("archived") ? "up" : "down"} small text-dark`}></i>
                    </div>
                  </button>
                  {expandedFilters.has("archived") && (
                    <div className="p-2 border-top">
                      <select
                        className="form-select form-select-sm"
                        value={archivedFilter}
                        onChange={(e) => handleArchivedChange(e.target.value)}
                        disabled={loading}
                      >
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Is Active Filter */}
              {hasIsActive && (
                <div className="border rounded mt-2">
                  <button
                    type="button"
                    className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
                    onClick={() => toggleFilterExpansion("is_active")}
                    disabled={loading}
                  >
                    <span className="small text-dark">
                      <strong>Is Active</strong>
                    </span>
                    <div className="d-flex align-items-center">
                      {isActiveFilter !== "both" && (
                        <span className="small text-muted me-2">
                          {isActiveFilter === "true" ? "True" : isActiveFilter === "false" ? "False" : "Both"}
                        </span>
                      )}
                      <i className={`ti ti-chevron-${expandedFilters.has("is_active") ? "up" : "down"} small text-dark`}></i>
                    </div>
                  </button>
                  {expandedFilters.has("is_active") && (
                    <div className="p-2 border-top">
                      <select
                        className="form-select form-select-sm"
                        value={isActiveFilter}
                        onChange={(e) => handleIsActiveChange(e.target.value)}
                        disabled={loading}
                      >
                        <option value="both">Both</option>
                        <option value="true">True</option>
                        <option value="false">False</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Dynamic UI Filters */}
              {fields.map((field) => {
                const fieldKey = getFieldKey(field);
                const filterValue = dynamicFilters[fieldKey];
                let displayValue = "";
                if (showFieldPreview && filterValue !== undefined && filterValue !== null && filterValue !== "") {
                  if (field === "Language") {
                    displayValue = languageOptions.find((opt) => String(opt.value) === String(filterValue))?.label || "";
                  } else if (field === "Currency") {
                    displayValue = currencyOptions.find((opt) => String(opt.value) === String(filterValue))?.label || "";
                  } else if (field === "Timezone") {
                    displayValue = timezoneOptions.find((opt) => String(opt.value) === String(filterValue))?.label || "";
                  } else if (field === "Assigned Team") {
                    displayValue = teams.find((opt) => String(opt.value) === String(filterValue))?.label || "";
                  } else if (field === "Assigned User") {
                    displayValue = users.find((opt) => String(opt.value) === String(filterValue))?.label || "";
                  } else if (field === "Company") {
                    displayValue = companies.find((opt) => String(opt.value) === String(filterValue))?.label || "";
                  } else if (field === "Source") {
                    displayValue = lifecycleSources.find((opt) => String(opt.value) === String(filterValue))?.label || "";
                  } else if (field === "Related Leads") {
                    displayValue = leads.find((opt) => String(opt.value) === String(filterValue))?.label || "";
                  } else {
                    displayValue = String(filterValue);
                  }
                }

                return (
                  <div className={`border rounded mt-2${!showFieldPreview ? " mb-3" : ""}`} key={field}>
                    <button
                      type="button"
                      className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
                      onClick={() => toggleFilterExpansion(field)}
                    >
                      <span className="small text-dark">
                        <strong>{getFieldDisplayLabel(field)}</strong>
                      </span>
                      <div className="d-flex align-items-center">
                        {displayValue && (
                          <span className="small text-muted me-2" title={displayValue}>
                            {truncateText(displayValue)}
                          </span>
                        )}
                        <i className={`ti ti-chevron-${expandedFilters.has(field) ? "up" : "down"} small text-dark`}></i>
                      </div>
                    </button>
                    {expandedFilters.has(field) && (
                      <div className="p-2 border-top">
                        {field === "Priority" ? (
                          <select
                            className="form-select form-select-sm"
                            value={dynamicFilters.priority || ""}
                            onChange={(e) => handleDynamicFilterChange("priority", e.target.value)}
                          >
                            <option value="">Select Priority</option>
                            <option value="High">High</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                          </select>
                        ) : field === "Language" ? (
                          <select
                            className="form-select form-select-sm"
                            value={dynamicFilters.preferred_language || ""}
                            onChange={(e) => handleDynamicFilterChange("preferred_language", e.target.value)}
                          >
                            <option value="">Select Language</option>
                            {languageOptions.map((opt: any) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : field === "Currency" ? (
                          <select
                            className="form-select form-select-sm"
                            value={dynamicFilters.currency_code || ""}
                            onChange={(e) => handleDynamicFilterChange("currency_code", e.target.value)}
                          >
                            <option value="">Select Currency</option>
                            {currencyOptions.map((opt: any) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : field === "Timezone" ? (
                          <select
                            className="form-select form-select-sm"
                            value={dynamicFilters.time_zone || ""}
                            onChange={(e) => handleDynamicFilterChange("time_zone", e.target.value)}
                          >
                            <option value="">Select Timezone</option>
                            {timezoneOptions.map((opt: any) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : field === "Source" ? (
                          <select
                            className="form-select form-select-sm"
                            value={dynamicFilters.lifecycle_source_id || ""}
                            onChange={(e) => handleDynamicFilterChange("lifecycle_source_id", e.target.value)}
                          >
                            <option value="">Select Source</option>
                            {lifecycleSources.map((opt: any) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : field === "Assigned Team" ? (
                          <select
                            className="form-select form-select-sm"
                            value={dynamicFilters.assigned_team_id || ""}
                            onChange={(e) => handleDynamicFilterChange("assigned_team_id", e.target.value)}
                          >
                            <option value="">Select Team</option>
                            {teams.map((opt: any) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : field === "Assigned User" ? (
                          <select
                            className="form-select form-select-sm"
                            value={dynamicFilters.assigned_user_id || ""}
                            onChange={(e) => handleDynamicFilterChange("assigned_user_id", e.target.value)}
                          >
                            <option value="">Select User</option>
                            {users.map((opt: any) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : field === "Company" ? (
                          <select
                            className="form-select form-select-sm"
                            value={dynamicFilters.company_id || ""}
                            onChange={(e) => handleDynamicFilterChange("company_id", e.target.value)}
                          >
                            <option value="">Select Company</option>
                            {companies.map((opt: any) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : field === "Related Leads" ? (
                          <select
                            className="form-select form-select-sm"
                            value={dynamicFilters.related_lead_id || ""}
                            onChange={(e) => handleDynamicFilterChange("related_lead_id", e.target.value)}
                          >
                            <option value="">Select Related Lead</option>
                            {leads.map((opt: any) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : field === "Related Pipelines" ? (
                          <select
                            className="form-select form-select-sm"
                            value={dynamicFilters.related_pipeline_id || ""}
                            onChange={(e) => handleDynamicFilterChange("related_pipeline_id", e.target.value)}
                          >
                            <option value="">Select Pipeline</option>
                            {pipelines.map((opt: any) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : field === "Related Deals" ? (
                          <select
                            className="form-select form-select-sm"
                            value={dynamicFilters.related_deal_id || ""}
                            onChange={(e) => handleDynamicFilterChange("related_deal_id", e.target.value)}
                          >
                            <option value="">Select Related Deal</option>
                            {deals.map((opt: any) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : field === "Related Proposal" ? (
                          <select
                            className="form-select form-select-sm"
                            value={dynamicFilters.related_proposal_id || ""}
                            onChange={(e) => handleDynamicFilterChange("related_proposal_id", e.target.value)}
                          >
                            <option value="">
                              Select Related {getLifecycleTypeName("PROPOSAL")}
                            </option>
                            {proposals.map((opt: any) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        ) : field === "Start Date & Time" || field === "Expiration Date & Time" || field === "Due Date & Time" ? (
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={dynamicFilters[fieldKey] || ""}
                            onChange={(e) => handleDynamicFilterChange(fieldKey, e.target.value)}
                          />
                        ) : (
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={`Enter ${field}`}
                            value={textFilters[fieldKey] !== undefined ? textFilters[fieldKey] : dynamicFilters[fieldKey] || ""}
                            onChange={(e) => handleTextChange(fieldKey, e.target.value)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {showFieldPreview && <br></br>}

          {/* Action Buttons */}
          {(!conditionalFooter || activeFilterCount > 0) && (
            <div className="p-3 pt-2 border-top" style={{ backgroundColor: "white" }}>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className={`btn btn-outline-secondary btn-sm flex-fill${!conditionalFooter ? " w-100" : ""}`}
                  onClick={resetFilters}
                  disabled={loading}
                >
                  <i className="ti ti-refresh me-1"></i>
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LifecycleFilterDropdown;
